import React from "react";
import { useField } from "formik";
import {
    RuleConditionDate,
    TransferRuleDirection,
} from "@common/types/transactionRule";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import {
    DateConditionType,
    TransactionRuleConditionFormSchema,
} from "../constants";
import { getFieldNamePrefix } from "../../utils";

interface Props {
    index: number;
    transferDirection?: TransferRuleDirection;
    container?: HTMLElement;
    controlButtonSize?: TriggerButtonProps["size"];
}

const conditionTypeItems = Object.values(DateConditionType).map((value) => ({
    value,
    label: value,
}));

export const DateConditionForm: React.FC<Props> = ({
    index,
    transferDirection,
    container,
    controlButtonSize,
}) => {
    const fieldName = getFieldNamePrefix(index, transferDirection);
    const [conditionTypeField, , conditionTypeHelpers] = useField<
        TransactionRuleConditionFormSchema["dateConditionType"]
    >(`${fieldName}.dateConditionType`);
    const [valueRangeField, , valueRangeHelpers] = useField<
        TransactionRuleConditionFormSchema["dateValueRange"]
    >(`${fieldName}.dateValueRange`);

    const setRangeValue = (
        value: string | undefined,
        key: keyof RuleConditionDate,
    ) => {
        valueRangeHelpers.setValue({
            ...(valueRangeField.value ?? {}),
            [key]: value,
        });
    };

    const datePickerButtonProps = {
        variant: "primary",
        size: controlButtonSize ?? "md",
    } satisfies TriggerButtonProps;

    return (
        <>
            <Picker.Select
                items={conditionTypeItems}
                value={conditionTypeField.value ?? null}
                onValueChange={(v) => {
                    if (v !== null) {
                        conditionTypeHelpers.setValue(v);
                    }
                }}
                placeholder="Select type"
                container={container}
                trigger={
                    <Picker.Button
                        variant="primary"
                        size={controlButtonSize ?? "md"}
                    />
                }
            />

            {conditionTypeField.value === DateConditionType.BEFORE && (
                <DatePicker
                    mode="day"
                    variant="button"
                    showTriggerIcon={false}
                    value={valueRangeField.value?.before}
                    onValueChange={(v) => setRangeValue(v, "before")}
                    buttonProps={datePickerButtonProps}
                />
            )}
            {conditionTypeField.value === DateConditionType.AFTER && (
                <DatePicker
                    mode="day"
                    variant="button"
                    showTriggerIcon={false}
                    value={valueRangeField.value?.after}
                    onValueChange={(v) => setRangeValue(v, "after")}
                    buttonProps={datePickerButtonProps}
                />
            )}
            {conditionTypeField.value === DateConditionType.BETWEEN && (
                <>
                    <DatePicker
                        mode="day"
                        variant="button"
                        showTriggerIcon={false}
                        value={valueRangeField.value?.after}
                        onValueChange={(v) => setRangeValue(v, "after")}
                        buttonProps={datePickerButtonProps}
                    />
                    and
                    <DatePicker
                        mode="day"
                        variant="button"
                        showTriggerIcon={false}
                        value={valueRangeField.value?.before}
                        onValueChange={(v) => setRangeValue(v, "before")}
                        buttonProps={datePickerButtonProps}
                    />
                </>
            )}
        </>
    );
};
